import React from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, QrCode, Smartphone, Zap } from 'lucide-react';
import Footer from '../components/Footer';

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Header */}
      <header className="px-4 md:px-8 py-4 flex items-center justify-between max-w-6xl mx-auto">
        <h1 className="font-heading font-bold text-xl text-[#F5C542]">Golden Tips</h1>
        <div className="flex items-center gap-3">
          <Link to="/login" className="btn-secondary !py-2" data-testid="header-login-button">
            Sign In
          </Link>
          <Link to="/signup" className="btn-primary !py-2 !px-5" data-testid="header-signup-button">
            Get Started
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="px-4 md:px-8 py-16 md:py-24 text-center max-w-4xl mx-auto">
        <h2 className="text-4xl md:text-6xl font-heading font-bold mb-6 leading-tight">
          Get tipped <span className="text-[#F5C542]">instantly</span>
        </h2>
        <p className="text-lg md:text-xl text-[#A1A1AA] mb-10 max-w-2xl mx-auto">
          Create your personal Golden Tips link in minutes. Share a QR code or link, and start receiving tips directly to your bank account.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to="/signup" className="btn-primary !w-auto !px-8" data-testid="hero-cta-button">
            Create your Golden Tips link
          </Link>
          <p className="text-[#52525B] text-sm">Free to set up • 3% platform fee</p>
        </div>
        <p className="mt-4 text-xs text-[#52525B] max-w-lg mx-auto" data-testid="challenge-fee-disclosure-landing">
          Opt into the{' '}
          <Link to="/dashboard/leaderboard" className="text-[#F5C542]/80 hover:text-[#F5C542] hover:underline">
            Top Earner Challenge
          </Link>{' '}
          for a shot at monthly prize pools — participants pay an extra 1% during periods they&apos;re enrolled.{' '}
          <Link to="/terms#challenge-rules" className="text-[#F5C542]/80 hover:text-[#F5C542] hover:underline" data-testid="challenge-rules-link-landing">
            See full rules →
          </Link>
        </p>
      </section>

      {/* Features */}
      <section className="px-4 md:px-8 py-16 max-w-5xl mx-auto">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="stat-card">
            <div className="w-12 h-12 rounded-xl bg-[#F5C542]/10 flex items-center justify-center mb-4">
              <QrCode size={24} className="text-[#F5C542]" />
            </div>
            <h3 className="font-heading font-bold text-lg mb-2">QR Code</h3>
            <p className="text-[#A1A1AA] text-sm">
              Print and display your unique QR code. Guests scan and tip in seconds.
            </p>
          </div>
          
          <div className="stat-card">
            <div className="w-12 h-12 rounded-xl bg-[#F5C542]/10 flex items-center justify-center mb-4">
              <Smartphone size={24} className="text-[#F5C542]" />
            </div>
            <h3 className="font-heading font-bold text-lg mb-2">No App Needed</h3>
            <p className="text-[#A1A1AA] text-sm">
              Guests don't need to download anything or create an account. Zero friction.
            </p>
          </div>
          
          <div className="stat-card">
            <div className="w-12 h-12 rounded-xl bg-[#F5C542]/10 flex items-center justify-center mb-4">
              <Zap size={24} className="text-[#F5C542]" />
            </div>
            <h3 className="font-heading font-bold text-lg mb-2">Instant Payouts</h3>
            <p className="text-[#A1A1AA] text-sm">
              Tips go directly to your connected bank account via Stripe. Fast and secure.
            </p>
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="px-4 md:px-8 py-16 max-w-4xl mx-auto text-center">
        <h3 className="text-2xl md:text-3xl font-heading font-bold mb-8">Perfect for</h3>
        <div className="flex flex-wrap justify-center gap-3">
          {['Bartenders', 'Servers', 'Baristas', 'Food Trucks', 'DJs', 'Valets', 'Hair Stylists', 'Musicians', 'Tour Guides', 'Personal Trainers'].map((role) => (
            <span key={role} className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm">
              {role}
            </span>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 md:px-8 py-16 text-center">
        <div className="max-w-md mx-auto bg-gradient-to-b from-[#F5C542]/10 to-transparent rounded-3xl p-8 border border-[#F5C542]/20">
          <div className="w-16 h-16 rounded-full bg-[#F5C542]/20 flex items-center justify-center mx-auto mb-6">
            <DollarSign size={32} className="text-[#F5C542]" />
          </div>
          <h3 className="text-2xl font-heading font-bold mb-3">Ready to start?</h3>
          <p className="text-[#A1A1AA] mb-6">Set up takes less than 2 minutes.</p>
          <Link to="/signup" className="btn-primary" data-testid="bottom-cta-button">
            Create your Golden Tips link
          </Link>
        </div>
      </section>

      {/* Footer */}
      <Footer className="mt-auto" />
    </div>
  );
}
